import rmskincareAPI from '../utils/rmskincareAPI'

const conversationApi = {
  /**
   * Fetch full message history for a client / appointment.
   * GET /management/conversation?customerId=X&appointmentId=Y
   */
  getHistory: async ({ customerId, appointmentId } = {}) => {
    const params = new URLSearchParams()
    if (customerId)    params.set('customerId',    customerId)
    if (appointmentId) params.set('appointmentId', appointmentId)
    const result = await rmskincareAPI.get(`/management/conversation?${params.toString()}`)
    return result.data.data || []
  },

  /**
   * Fetch inbox conversation list (all customers with at least one message).
   * GET /management/conversation/inbox?storeId=X&status=open
   * Returns: [{ customerId, customerName, customerPhone, lastMessage, unreadCount, status }]
   */
  getInboxList: async ({ storeId, status = 'open' } = {}) => {
    const params = new URLSearchParams()
    if (storeId) params.set('storeId', storeId)
    params.set('status', status)
    const result = await rmskincareAPI.get(`/management/conversation/inbox?${params.toString()}`)
    return result.data.data || []
  },

  /**
   * Update conversation status for a customer.
   * POST /management/conversation/status { customerId, status }
   * status: 'open' | 'closed' | 'spam'
   */
  setConversationStatus: async (customerId, status) => {
    await rmskincareAPI.post('/management/conversation/status', { customerId, status })
  },

  /**
   * Mark all messages from a customer as read.
   * POST /management/conversation/read { customerId }
   */
  markRead: async (customerId) => {
    await rmskincareAPI.post('/management/conversation/read', { customerId })
  },

  /**
   * Send an SMS to a client.
   * POST /management/conversation/send
   */
  sendSms: async (payload) => {
    const result = await rmskincareAPI.post('/management/conversation/send', payload)
    return result.data.data
  },

  /**
   * Open an SSE stream for real-time inbound message push (per conversation).
   * GET /management/conversation/stream?customerId=X&appointmentId=Y&token=JWT
   * Returns the EventSource instance — call .close() to disconnect.
   */
  subscribeToMessages: ({ customerId, appointmentId, onMessage, onError }) => {
    const token = localStorage.getItem('accessToken') || ''
    const params = new URLSearchParams({ token })
    if (customerId)    params.set('customerId',    customerId)
    if (appointmentId) params.set('appointmentId', appointmentId)
    const url = `${process.env.REACT_APP_API_URL}/management/conversation/stream?${params.toString()}`
    const es = new EventSource(url)
    es.onmessage = (e) => {
      try { onMessage(JSON.parse(e.data)) } catch { /* ignore malformed frames */ }
    }
    if (onError) es.onerror = onError
    return es
  },

  /**
   * Open a global SSE stream that pushes ANY new inbound message across all customers.
   * GET /management/conversation/inbox/stream?token=JWT
   * Used by the TopBar to show real-time unread badge counts.
   * Returns the EventSource instance — call .close() to disconnect.
   *
   * Each event data: { customerId, customerName, body, createdAt, conversationId }
   */
  subscribeToInboxStream: ({ onMessage, onError }) => {
    const token = localStorage.getItem('accessToken') || ''
    const url = `${process.env.REACT_APP_API_URL}/management/conversation/inbox/stream?token=${encodeURIComponent(token)}`
    const es = new EventSource(url)
    es.onmessage = (e) => {
      try { onMessage(JSON.parse(e.data)) } catch { /* ignore malformed frames */ }
    }
    if (onError) es.onerror = onError
    return es
  },
}

export default conversationApi
